/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module VsLogsModule
 */

import { isString, keys } from 'underscore';
import { toFixed } from 'ng/shared/utils';
import { defaultPageSize } from 'ng/modules/vs-logs/constants/default-values.constants';
import {
    IVsLogsCombinedRequestUpdatableParams,
    IVsLogsDefaultSignpostData,
    IVsLogsEndToEndTimingResponseData,
    IVsLogsEndToEndTimingSignpostData,
    IVsLogsGroupbyResponseData,
    IVsLogsGroupbyResponseResultData,
    IVsLogsGroupsSignpostData,
    IVsLogsPolicyGroupsResponseData,
    IVsLogsSignificanceCategoryResultsResponseData,
    IVsLogsSignificanceSignpostData,
    IVsLogsSignpostPhasesConfig,
    IVsLogsSignpostRange,
    IVsLogsSignpostTemplateConfig,
    IVsLogsSignpostTransformedResult,
    IVsLogsSignpostUpdatableParams,
    IVsLogsSslGroupsResponseData,
    IVsLogsWafLatencyResponseData,
    IVsLogsWafLatencySignpostData,
    IVsLogsWafRuleGroupsRules,
    IVsLogsWafRuleGroupsSignpostData,
    IVsLogsWafRuleSignpostData,
    TVsLogsSignpostApiResponseData,
    TVsLogsSignpostStoreData,
} from '../vs-logs.types';

import {
    customTemplates,
    defaultSignpostParams,
    e2eTimingGroupByKeys,
    e2eTimingL4KeysOrderedList,
    e2eTimingL7KeysOrderedList,
    signpostsWithRange,
    templatesConfiguration,
    wafLatencyPhases,
} from '../constants/vs-logs-signpost.constants';

const wafRuleGroupsIdKey = 'waf_log.rule_logs.rule_id';
const wafRuleGroupsNameKey = 'waf_log.rule_logs.rule_name';
const wafRuleGroupsGroupKey = 'waf_log.rule_logs.rule_group';
const nonSignificantLogKey = 'Non-Significant Log';
const E2E_AVERAGE_REQUEST_ID = 'average';
const E2E_GRID_RESULT_REQUEST_ID = 'e2eResult';

/**
 * Redirect to respective tranformation logic depending on type.
 */
export const transformSignpostData = (
    data: TVsLogsSignpostApiResponseData,
    configKey: string,
): TVsLogsSignpostStoreData => {
    switch (configKey) {
        case customTemplates.wafPhaseLatency:
            return transformWafLatencyResults(data as IVsLogsWafLatencyResponseData);

        case customTemplates.wafRuleGroups:
            return transformWafRuleGroupsResults(data as IVsLogsGroupbyResponseData);

        case customTemplates.policy:
            return transformPolicySignpostResults(data as IVsLogsPolicyGroupsResponseData);

        case customTemplates.ssl:
            return transformSslSignpostResults(data as IVsLogsSslGroupsResponseData);

        case customTemplates.significance:
            return transformSignificanceSignpostResults(
                data as IVsLogsGroupbyResponseData,
            );

        case customTemplates.e2eTiming:
            return transformEndToEndTimingSignpostResults(
                data as IVsLogsEndToEndTimingResponseData,
            );

        case customTemplates.wafRule:
            return transformWafRuleSignpostResults(
                data as IVsLogsGroupbyResponseData,
            );

        default:
            return transformDefaultSignpostData(data as IVsLogsGroupbyResponseData, configKey);
    }
};

/**
 * Transform received results for default template in the form usable for displaying.
 */
export const transformDefaultSignpostData = (
    data: IVsLogsGroupbyResponseData,
    configKey: string,
): TVsLogsSignpostStoreData => {
    if (signpostsWithRange.has(configKey)) {
        const groupby = templatesConfiguration[configKey].params;

        return transformResultsWithRange(data.results, groupby);
    }

    return transformResultsForValue(data.results, configKey);
};

/**
 *  Transform results, append attributes which are required for displaying default signpost.
 */
export const transformWafRuleSignpostResults = (
    data: IVsLogsGroupbyResponseData,
): IVsLogsWafRuleSignpostData[] => {
    const results = data.results || [];

    // We only show top 10 entries in signpost.
    if (results.length > 10) {
        results.length = 10;
    }

    const configKey = customTemplates.wafRule;
    const { params = [] } = templatesConfiguration[configKey];
    const transformedResult: IVsLogsWafRuleSignpostData[] = [];

    results.forEach((response: IVsLogsGroupbyResponseResultData) => {
        const values = params.map((param: string) => {
            const value = response[param];
            const valueObj = {
                value,
                param,
                isValid: Boolean(value),
            };

            return valueObj;
        });

        const resObj: IVsLogsWafRuleSignpostData = {
            count: Number(response.count),
            percentage: Number(response.percentage?.toFixed(2)),
            values,
        };

        transformedResult.push(resObj);
    });

    return transformedResult;
};

/**
 * Transform results for signpost having range type.
 */
export const transformResultsWithRange = (
    results: IVsLogsGroupbyResponseResultData[] = [],
    groupby: string,
): IVsLogsDefaultSignpostData[] => {
    const transformedResult: IVsLogsDefaultSignpostData[] = [];

    // We only show top 10 entries in signpost.
    if (results.length > 10) {
        results.length = 10;
    }

    results.forEach(res => {
        const value = res[groupby];

        if (value) {
            const { start, end } = value;

            res.value = {
                value,
                param: groupby,
                isValid: !(start === undefined || end === undefined || isNegativeRange(value)),
            };

            transformedResult.push({
                value: res.value,
                count: Number(res.count),
                percentage: Number(res.percentage?.toFixed(2)),
            });
        }
    });

    return transformedResult;
};

/**
 * Return true if time-range has negative range data.
 */
export const isNegativeRange = (range: IVsLogsSignpostRange): boolean => {
    const { start, end } = range;

    return start && end && Number(start) < 0 && Number(end) < 0;
};

/**
 *  Transform results, append attributes which are required for displaying default signpost.
 */
export const transformResultsForValue = (
    results: IVsLogsGroupbyResponseResultData[] = [],
    configKey: string,
): IVsLogsDefaultSignpostData[] => {
    // We only show top 10 entries in signpost.
    if (results.length > 10) {
        results.length = 10;
    }

    const { params } = templatesConfiguration[configKey];
    const transformedResult: IVsLogsDefaultSignpostData[] = results.map(
        (record: IVsLogsGroupbyResponseResultData) => {
            const resObj: IVsLogsDefaultSignpostData = {
                count: Number(record.count),
                percentage: Number(record.percentage?.toFixed(2)),
                value: selectValue(params, record),
            };

            return resObj;
        },
    ) || [];

    return transformedResult;
};

/**
 *  Transform results, append attributes which are required for displaying significance signpost.
 */
export const transformSignificanceSignpostResults = (
    data: IVsLogsGroupbyResponseData,
): IVsLogsSignificanceSignpostData[] => {
    const categoryResults =
        data.category_results as IVsLogsSignificanceCategoryResultsResponseData[] || [];
    const configKey = customTemplates.significance;

    // We only show top 10 entries in signpost.
    if (categoryResults.length > 10) {
        categoryResults.length = 10;
    }

    const { params: param } = templatesConfiguration[configKey];
    const transformedResult: IVsLogsSignificanceSignpostData[] = [];

    categoryResults.forEach((parent: IVsLogsSignificanceCategoryResultsResponseData) => {
        const nonSignificant = parent.primary_reason === nonSignificantLogKey;
        const parentValue = nonSignificant ? '' : parent.primary_reason;
        const children = parent.secondary_reasons?.map(
            (secondaryReason: IVsLogsGroupbyResponseResultData) => {
                const value = secondaryReason[param];
                const display = `${parentValue}: ${value}`;
                const child: IVsLogsDefaultSignpostData = {
                    count: secondaryReason.count,
                    percentage: secondaryReason.percentage,
                    value: {
                        value: display,
                        display,
                        contains: true,
                        param,
                        isValid: Boolean(display),
                    },
                };

                return child;
            },
        ) || [];

        const resObj: IVsLogsSignificanceSignpostData = {
            children,
            count: parent.count,
            percentage: parent.percentage,
            value: {
                display: parent.primary_reason,
                value: parentValue,
                contains: nonSignificant ? undefined : true,
                param,
                isValid: Boolean(parent.primary_reason),
            },
        };

        transformedResult.push(resObj);
    });

    return transformedResult;
};

/**
 * Transform received results for WAF groups template in the form usable for displaying WAF rules
 * group signpost.
 */
export const transformWafRuleGroupsResults = (
    data: IVsLogsGroupbyResponseData,
): IVsLogsWafRuleGroupsSignpostData[] => {
    const wafRuleGroups = {};
    const { results = [] } = data;
    const transformedResult: IVsLogsWafRuleGroupsSignpostData[] = [];

    // We only show top 10 entries in signpost.
    if (results.length > 10) {
        results.length = 10;
    }

    results.forEach(item => {
        const { count = 0, percentage = 0 } = item;
        const ruleId = item[wafRuleGroupsIdKey];
        const ruleName = item[wafRuleGroupsNameKey];
        const groupName = item[wafRuleGroupsGroupKey];
        const wafRuleGroup = wafRuleGroups[groupName];

        if (groupName) {
            const rules: IVsLogsWafRuleGroupsRules[] = wafRuleGroup ? wafRuleGroup.rules : [];
            const totalCount = (wafRuleGroup ? wafRuleGroup.groupCount : 0) + count;

            rules.push({
                ruleId: {
                    value: ruleId,
                    param: wafRuleGroupsIdKey,
                },
                ruleName: {
                    value: ruleName,
                    param: wafRuleGroupsNameKey,
                },
                count,
                percentage,
            });

            wafRuleGroups[groupName] = {
                rules,
                groupCount: totalCount,
            };
        }
    });

    Object.keys(wafRuleGroups).forEach(groupName => {
        const { rules } = wafRuleGroups[groupName];
        const { groupCount } = wafRuleGroups[groupName].groupCount;
        const resObj: IVsLogsWafRuleGroupsSignpostData = {
            children: rules,
            name: groupName,
            count: groupCount,
            value: {
                value: groupName,
                param: wafRuleGroupsGroupKey,
            },
        };

        transformedResult.push(resObj);
    });

    return transformedResult;
};

/**
 * Transform received results for WAF latency template in the form usable for displaying
 * WAF latency signpost.
 */
export const transformWafLatencyResults = (
    combinedData: IVsLogsWafLatencyResponseData,
): IVsLogsWafLatencySignpostData => {
    const configKey = customTemplates.wafPhaseLatency;
    const { phases } = templatesConfiguration[configKey];
    const transformedResultObj: IVsLogsWafLatencySignpostData = {
        requestHeader: [],
        requestBody: [],
        responseHeader: [],
        responseBody: [],
    };

    if (combinedData) {
        Object.values(combinedData).forEach((singleRequestData, index) => {
            const { name, groupby } = phases[index];

            transformedResultObj[wafLatencyPhases[index]] =
                singleRequestData.results?.map((res: IVsLogsGroupbyResponseResultData) => {
                    const value = res[groupby];

                    return {
                        value,
                        param: groupby,
                        isValid: Boolean(value),
                        count: res.count,
                        percentage: res.percentage,
                        phase: name,
                    };
                }) || [];
        });
    }

    return transformedResultObj;
};

/**
 * Transform received results for e2e timing template in the form usable for displaying
 * e2e signpost.
 */
export const transformEndToEndTimingSignpostResults = (
    combinedData: IVsLogsEndToEndTimingResponseData,
): IVsLogsEndToEndTimingSignpostData => {
    const [averageData] = combinedData.average?.results || [];
    const e2eResult = combinedData.e2eResult?.results;
    const transformedResultObj: IVsLogsEndToEndTimingSignpostData = {};

    /**
     * Transform average data from the API to usable values in signpost.
     * Backend does not return data in key value pairs with the key as the prop name,
     * rather it returns the data in an indexed format, where the index matches the
     * order of the requested params. Hence we determine what was asked for based on
     * count, and then match the values to the appropriate keys.
     */
    if (averageData) {
        /**
         * Determine the number of columns ('col[num]') returned by the API.
         * It may be much simpler/faster to use VirtualServiceStateService
         * to determine the present type of VS (L4/L7), however we want to maintain
         * stateless nature of our utils file.
         */
        const numberOfAverageDataCols =
            keys(averageData).reduce((acc, val) => {
                if (val.includes('col')) {
                    acc++;
                }

                return acc;
            }, 0);

        let orderedKeyList: string[] = [];

        /**
         * Determine the ordered key list (originally dictated by VS type)
         * based on the number of columns ('col[num]') returned by the API.
         */
        switch (numberOfAverageDataCols) {
            case e2eTimingL7KeysOrderedList.length:
                orderedKeyList = e2eTimingL7KeysOrderedList;
                break;
            case e2eTimingL4KeysOrderedList.length:
                orderedKeyList = e2eTimingL4KeysOrderedList;
                break;
        }

        transformedResultObj.average = {};

        /**
         * Backend returns the average e2e data without properly assigned keys,
         * but with the same indexing we provided in the request params. Hence we can use the
         * e2eTimingL7KeysOrderedList or e2eTimingL4KeysOrderedList to assign the
         * values from api response in the same order as e2eTimingL7KeysOrderedList maintains.
         */
        orderedKeyList.forEach((value: string, i: number) => {
            const apiVal = averageData[`col${i}`];

            if (apiVal !== undefined) {
                transformedResultObj.average[value] = toFixed(apiVal);
            }
        });
    }

    if (e2eResult) {
        // Calculating current groupby key, we take the 1 result object and find groupby.
        // If no key is found then we assign total_time as the default groupby for signpost.
        const resultKeys = Object.keys(e2eResult[0]);
        const groupby = resultKeys.find(
            key => Object.values(e2eTimingGroupByKeys).includes(key),
        ) || e2eTimingGroupByKeys.totalTime;

        transformedResultObj.e2eResult = transformResultsWithRange(e2eResult, groupby);
    }

    return transformedResultObj;
};

/**
 * Transform received results for WAF latency template in the form usable for displaying
 * policy signpost.
 */
export const transformPolicySignpostResults = (
    combinedData: IVsLogsPolicyGroupsResponseData,
): IVsLogsGroupsSignpostData[] => {
    const configKey = customTemplates.policy;
    const template = templatesConfiguration[configKey];
    const transformedResult: IVsLogsGroupsSignpostData[] = [];

    Object.keys(template.query).forEach(queryKey => {
        const singleRequestData = combinedData[queryKey];

        if (singleRequestData) {
            const children = singleRequestData.results?.map(
                (val: IVsLogsGroupbyResponseResultData) => {
                    const param: string = template.query[queryKey];
                    const currentValue: string = val[param];
                    const resObj: IVsLogsDefaultSignpostData = {
                        count: val.count,
                        percentage: val.percentage,
                        value: {
                            value: currentValue,
                            param,
                            isValid: Boolean(currentValue),
                        },
                    };

                    return resObj;
                },
            ) || [];

            transformedResult.push({
                name: template.subheader[queryKey],
                children,
            });
        }
    });

    return transformedResult;
};

/**
 * Transform received results for ssl in the form usable for displaying ssl signpost.
 */
export const transformSslSignpostResults = (
    combinedData: IVsLogsSslGroupsResponseData,
): IVsLogsGroupsSignpostData[] => {
    const configKey = customTemplates.ssl;
    const template = templatesConfiguration[configKey];
    const transformedResult: IVsLogsGroupsSignpostData[] = [];

    // Two types of data.
    const { ssl, other } = combinedData;

    // Ssl: 'ssl_version' section.
    if (ssl) {
        const { results: sslVersionResults = [] } = ssl;

        const children = sslVersionResults.map((response: IVsLogsGroupbyResponseResultData) => {
            const sslVersionKey: string = template.query.ssl;
            const resObj: IVsLogsDefaultSignpostData = {
                count: response.count,
                percentage: response.percentage,
                value: {
                    value: response[sslVersionKey],
                    param: sslVersionKey,
                    isValid: Boolean(response[sslVersionKey]),
                },
            };

            return resObj;
        });

        transformedResult.push({
            name: template.subheader[customTemplates.ssl],
            children,
        });
    }

    // Other: 'ssl_cipher' section.
    if (other) {
        const sslCipherKeys = template.key;

        Object.keys(sslCipherKeys).forEach(key => {
            const { category_results: categoryResults } = other;
            const prefix: string = sslCipherKeys[key];
            const sslCipherResults = categoryResults[sslCipherKeys[key]] || [];

            const children = sslCipherResults.map((response: IVsLogsGroupbyResponseResultData) => {
                const { value } = response;
                const sslCipherKey: string = template.query.other;
                const resObj: IVsLogsDefaultSignpostData = {
                    count: response.count,
                    percentage: response.percentage,
                    value: {
                        display: value,
                        value: `${prefix}:${value}`,
                        param: sslCipherKey,
                        isValid: Boolean(value),
                        contains: true,
                    },
                };

                return resObj;
            });

            transformedResult.push({
                name: template.subheader[key],
                children,
            });
        });
    }

    return transformedResult;
};

/**
 * Update value attribute of entries which have single param, value is used for displaying
 * default signpost.
 */
export const selectValue = (
    param: string,
    record: IVsLogsGroupbyResponseResultData,
): IVsLogsSignpostTransformedResult => {
    let value = record[param];
    const result: IVsLogsSignpostTransformedResult = {
        value,
        param,
        isValid: true,
    };
    const either = [
        ['client_ip', 'client_ip6'],
        ['vs_ip', 'vs_ip6'],
        ['server_ip', 'server_ip6'],
    ];

    either.forEach(([param2, param1]) => {
        if (param1 in record && record[param1] !== '') {
            value = record[param1];
            result.param = param1;
        } else if (param2 in record) {
            value = record[param2];
            result.param = param2;
        }
    });

    if (value === '' || value === 0 || value === '0') {
        result.isValid = false;
    }

    result.value = value;

    return result;
};

/**
 * Get request params for policy signpost.
 */
export const getPolicyRequestParams = (vs: any): IVsLogsCombinedRequestUpdatableParams[] => {
    const configKey = customTemplates.policy;
    const template = templatesConfiguration[configKey];
    const { query } = template;
    const { http_policies: httpPolicies } = vs.getConfig();
    const fullRequestsParams: IVsLogsCombinedRequestUpdatableParams[] = [];

    Object.entries(query).forEach(([key, value]) => {
        const groupby = value as string;
        const vsPolicy = httpPolicies[0] && httpPolicies[0].http_policy_set_ref_data &&
            httpPolicies[0].http_policy_set_ref_data[groupby.slice(0, -10)];

        if (vsPolicy && Array.isArray(vsPolicy.rules)) {
            fullRequestsParams.push({
                requestId: key,
                requestParams: {
                    ...defaultSignpostParams,
                    groupby,
                },
            });
        }
    });

    return fullRequestsParams;
};

/**
 * Get request params for ssl signpost.
 */
export const getSslRequestParams = (): IVsLogsCombinedRequestUpdatableParams[] => {
    const configKey = customTemplates.ssl;
    const template = templatesConfiguration[configKey];
    const { query } = template;
    const fullRequestsParams: IVsLogsCombinedRequestUpdatableParams[] = [];

    Object.entries(query).forEach(([key, value]) => {
        const groupby = value as string;

        fullRequestsParams.push({
            requestId: key,
            requestParams: {
                ...defaultSignpostParams,
                groupby,
            },
        });
    });

    return fullRequestsParams;
};

/**
 * Get request params for end to end timing signpost.
 */
export const getEndToEndTimingRequestParams = (
    key: string,
    vs: any,
): IVsLogsCombinedRequestUpdatableParams[] => {
    const template = templatesConfiguration[customTemplates.e2eTiming];
    const { query, page_size: pageSize } = template;
    const vsType = vs.isHTTP() ? '7' : '4'; // Number 7 and 4 stands for L7 and L4 VS type.
    const cols = query[`average${vsType}`];
    const fullRequestsParams: IVsLogsCombinedRequestUpdatableParams[] = [];

    fullRequestsParams.push({
        requestId: E2E_AVERAGE_REQUEST_ID,
        requestParams: {
            ...defaultSignpostParams,
            cols,
            groupby: 'full',
            orderby: null,
            page_size: pageSize || defaultPageSize,
        },
    });

    fullRequestsParams.push({
        requestId: E2E_GRID_RESULT_REQUEST_ID,
        requestParams: {
            ...defaultSignpostParams,
            groupby: key,
            orderby: key,
            step: key === e2eTimingGroupByKeys.clientRtt ||
                key === e2eTimingGroupByKeys.serverRtt ? '10' : '100',
            expstep: true,
        },
    });

    return fullRequestsParams;
};

/**
 * Get request params for significance signpost.
 */
export const getSignificanceRequestParams = (): IVsLogsSignpostUpdatableParams => {
    const template = templatesConfiguration[customTemplates.significance];
    const { queryParams: groupby, page_size: pageSize } = template;

    const fullSignpostParams: IVsLogsSignpostUpdatableParams = {
        ...defaultSignpostParams,
        groupby,
        page_size: pageSize || defaultPageSize,
    };

    return fullSignpostParams;
};

/**
 * Get request params for WAF latency signpost.
 */
export const getWafLatencyRequestParams = (): IVsLogsCombinedRequestUpdatableParams[] => {
    const template = templatesConfiguration['waf-phase-latency'];
    const { phases, page_size: pageSize } = template;

    const fullRequestsParams: IVsLogsCombinedRequestUpdatableParams[] = phases.map(
        ({ groupby }: IVsLogsSignpostPhasesConfig, index) => {
            return {
                requestId: wafLatencyPhases[index],
                requestParams: {
                    ...defaultSignpostParams,
                    groupby,
                    step: '100',
                    page_size: pageSize || defaultPageSize,
                    expstep: true,
                },
            };
        },
    );

    return fullRequestsParams;
};

/**
 * Get request params for WAF Rule signpost.
 */
export const getWafRuleRequestParams = (): IVsLogsSignpostUpdatableParams => {
    const template = templatesConfiguration[customTemplates.wafRule];
    const { queryParams, page_size: pageSize } = template;
    const groupby = queryParams.join(',');

    const fullSignpostParams: IVsLogsSignpostUpdatableParams = {
        ...defaultSignpostParams,
        groupby,
        page_size: pageSize || defaultPageSize,
    };

    return fullSignpostParams;
};

/**
 * Get request params for WAF rule groups signpost.
 */
export const getWafGroupsRequestParams = (): IVsLogsSignpostUpdatableParams => {
    const { wafRuleGroups: configKey } = customTemplates;
    const template = templatesConfiguration[configKey];
    const { queryParams, page_size: pageSize } = template;
    const groupby = queryParams.join(',');

    const fullSignpostParams: IVsLogsSignpostUpdatableParams = {
        ...defaultSignpostParams,
        groupby,
        page_size: pageSize || defaultPageSize,
    };

    return fullSignpostParams;
};

/**
 * Get request params for default signpost.
 */
export const getRequestParams = (
    activeTemplateConfig: IVsLogsSignpostTemplateConfig,
): IVsLogsSignpostUpdatableParams => {
    const { queryParams, page_size: pageSize } = activeTemplateConfig;
    let groupby: string;

    if (Array.isArray(queryParams)) {
        groupby = queryParams.join(',');
    } else if (isString(queryParams)) {
        groupby = queryParams;
    }

    const fullSignpostParams: IVsLogsSignpostUpdatableParams = {
        ...defaultSignpostParams,
        groupby,
        page_size: pageSize || defaultPageSize,
    };

    return fullSignpostParams;
};

/**
 * Get request params for default signpost with range type.
 */
export const getRLengthRequestParams = (
    requestParams: IVsLogsSignpostUpdatableParams,
    activeTemplateConfig: IVsLogsSignpostTemplateConfig,
    configKey: string,
): IVsLogsSignpostUpdatableParams => {
    const { params } = activeTemplateConfig;

    requestParams = {
        ...requestParams,
        step: '512',
        expstep: true,
        orderby: isString(params) ? `${params}` : '-count',
    };

    if (configKey === 'icap_log.request_logs.latency') {
        requestParams.step = '100';
        requestParams.orderby = '-count';
    }

    return requestParams;
};
